
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import SampleCode from 'src/components/docs/plaidoms/SampleCode';
import LinkTokenCreate from 'src/components/docs/LinkTokenCreate';
export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Learn how to onboard your users to Plaid Check and Consumer Report'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Add Consumer Report to your app' subtitle='Onboard your users to Plaid Check to generate cash flow insights' mdxType="Header" />
    <p>{`This guide will walk you through onboarding your users to Plaid Check, so you can generate cash flow insights for your customers by creating a Consumer Report.`}</p>
    <Callout mdxType="Callout">
      <p>{`This is designed as a granular, step-by-step walkthrough showing one option for a simple Plaid Check integration for customers new to Plaid. Plaid also offers a `}<a parentName="p" {...{
          "href": "/docs/check/#no-code-integration-with-the-credit-dashboard"
        }}>{`no-code integration option`}</a>{` and a `}<a parentName="p" {...{
          "href": "/docs/check/#standard-integration-flow"
        }}>{`high-level integration overview`}</a>{`.`}</p>
      <p>{`If you are migrating to Plaid Check Consumer Report from a different Plaid product, see the corresponding migration guide: `}<a parentName="p" {...{
          "href": "/docs/check/migrate-from-assets/"
        }}>{`Migrate from Assets`}</a>{`, `}<a parentName="p" {...{
          "href": "/docs/check/migrate-from-income/"
        }}>{`Migrate from Income`}</a>{`, or `}<a parentName="p" {...{
          "href": "/docs/check/migrate-from-transactions/"
        }}>{`Migrate from Transactions`}</a>{`.`}</p>
    </Callout>
    <h4 {...{
      "id": "install-and-initialize-plaid-libraries"
    }}>{`Install and initialize Plaid libraries`}</h4>
    <p>{`You can use our official server-side client libraries to connect to the Plaid Check API from your application:`}</p>
    <SampleCode.InstallClientLibraries />
    <p>{`After you've installed these client libraries, initialize them by passing in your `}<inlineCode parentName="p">{`client_id`}</inlineCode>{`, `}<inlineCode parentName="p">{`secret`}</inlineCode>{`, and the environment you wish to connect to (Sandbox or Production). This will make sure the client libraries pass along your `}<inlineCode parentName="p">{`client_id`}</inlineCode>{` and `}<inlineCode parentName="p">{`secret`}</inlineCode>{` with each request, and you won't need to explicitly include them in any other calls.`}</p>
    <SampleCode.InitializeClientLibraries />
    <h4 {...{
      "id": "create-a-user"
    }}>{`Create a user`}</h4>
    <p>{`Your next step is to create a `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` that will represent the end user.`}</p>
    <p>{`To create a `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`, make a call to the `}<inlineCode parentName="p">{`/user/create`}</inlineCode>{` endpoint. This endpoint requires a unique `}<inlineCode parentName="p">{`client_user_id`}</inlineCode>{` value to represent the current user. Typically, this value is the same identifier you're using in your own application to represent the currently signed-in user.`}</p>
    <p>{`In addition, you will need to pass in an `}<inlineCode parentName="p">{`identity`}</inlineCode>{` object to use Plaid Check products for this user. In this field, you should pass in user identity data that has been provided by your user for consumer report purposes. At minimum, the following fields must be provided:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`name`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`date_of_birth`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`emails`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`phone_numbers`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`addresses`}</inlineCode></li>
    </ul>
    <p>{`At least one email, phone number, and address must be designated as `}<inlineCode parentName="p">{`primary`}</inlineCode>{`.`}</p>
    <p>{`If you intend to share the report with a GSE (Government-Sponsored Enterprise) such as Fannie or Freddie, the full SSN is also required via the `}<inlineCode parentName="p">{`id_numbers`}</inlineCode>{` field. For all use cases, providing at least a partial SSN is highly recommended, since it improves the accuracy of matching user records during compliance processes such as file disclosure, dispute, or security freeze requests.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`/user/create`}</inlineCode>{` endpoint will return a randomly generated string for the `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`. Make sure to save it. You send the `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` to Plaid Check to generate reports, and Plaid Check will use it to refer to this user when it sends you a webhook.`}</p>
    <p>{`Depending on your application, you might wish to create this `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` as soon as your user creates an account, or wait until shortly before your application would want to generate a report for this user.`}</p>
    <h4 {...{
      "id": "create-a-link-token"
    }}>{`Create a Link token`}</h4>
    <p>{`Link is a client side UI widget that provides a secure, elegant flow to guide your users through the process of connecting Plaid Check with their financial institutions.`}</p>
    <p>{`Before initializing Link, you will need to create a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{`. A `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` is a short-lived, single-use token that is used to authenticate your app with Link. You can create one using the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` endpoint from your server.`}</p>
    <p>{`When calling `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`, you'll include an object telling Plaid Check about your application as well as how to customize the Link experience. In addition to the required parameters, you will need to provide the following:`}</p>
    <ul>
      <li parentName="ul">{`For `}<inlineCode parentName="li">{`user_id`}</inlineCode>{`, provide the `}<inlineCode parentName="li">{`user_id`}</inlineCode>{` you created in the previous step.`}</li>
      <li parentName="ul">{`For `}<inlineCode parentName="li">{`products`}</inlineCode>{`, pass in `}<inlineCode parentName="li">{`cra_base_report`}</inlineCode>{`, plus any additional Plaid Check products you wish to use (`}<inlineCode parentName="li">{`cra_income_insights`}</inlineCode>{`, `}<inlineCode parentName="li">{`cra_cashflow_insights`}</inlineCode>{`, `}<inlineCode parentName="li">{`cra_lend_score`}</inlineCode>{`, `}<inlineCode parentName="li">{`cra_partner_insights`}</inlineCode>{`, and/or `}<inlineCode parentName="li">{`cra_network_insights`}</inlineCode>{`), along with any additional Plaid Inc. products you are using. Note that Plaid Check products can't be used in the same Link flow with Income or Assets.`}</li>
      <li parentName="ul">{`Provide a `}<inlineCode parentName="li">{`webhook`}</inlineCode>{` URL with an endpoint where you can receive webhooks from Plaid Check. Plaid Check will contact this endpoint when your report is ready.`}</li>
      <li parentName="ul">{`Include the appropriate options for the products you are using in the `}<inlineCode parentName="li">{`cra_options`}</inlineCode>{` object.`}</li>
      <li parentName="ul">{`Include a `}<inlineCode parentName="li">{`consumer_report_permissible_purpose`}</inlineCode>{` parameter specifying your purpose for generating a Consumer Report.`}</li>
      <li parentName="ul">{`On mobile, specify the `}<inlineCode parentName="li">{`redirect_uri`}</inlineCode>{` and/or `}<inlineCode parentName="li">{`android_package_name`}</inlineCode>{` fields as necessary, per the relevant `}<a parentName="li" {...{
          "href": "/docs/link/"
        }}>{`Link documentation`}</a>{` for your platform.`}</li>
    </ul>
    <p>{`Send the `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` you get back in the response to your client application.`}</p>
    <LinkTokenCreate useCases={[{
      useCase: 'Consumer Report for credit and lending (Consumer Report by Plaid Check)',
      comment: 'Generate the user_id by calling /user/create',
      config: {
        user_id: 'usr_9nSp2KuZ2x4JDw',
        products: ['cra_income_insights', 'cra_base_report'],
        consumer_report_permissible_purpose: 'EXTENSION_OF_CREDIT',
        enable_multi_item_link: true,
        cra_options: {
          days_requested: 365
        },
        client_name: 'The Loan Arranger',
        language: 'en',
        country_codes: ['US'],
        webhook: 'https://sample-web-hook.com'
      }
    }]} filenameAsTitle={true} mdxType="LinkTokenCreate" />
    <h5 {...{
      "id": "using-plaid-check-products-alongside-plaid-products"
    }}>{`Using Plaid Check products alongside Plaid products`}</h5>
    <p>{`If you wish to use Plaid Inc. products (Transactions, Auth, etc.) alongside Plaid Check products, you can. You can just add those products to the appropriate products array when sending a request to `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`. Link will take the user through a hybrid flow, where we establish connections to both Plaid Check and Plaid Inc. at the same time. One exception is that you cannot use the Plaid Inc. Income or Assets products in the same session as a Plaid Check product. Instead, you should use the similar CRA Income Insights or Consumer Report (`}<inlineCode parentName="p">{`cra_base_report`}</inlineCode>{`) products.`}</p>
    <h4 {...{
      "id": "initialize-link"
    }}>{`Initialize Link`}</h4>
    <p>{`Note that these instructions cover Link on the web. For instructions on using Link within mobile apps, see the appropriate section in the `}<a parentName="p" {...{
        "href": "/docs/link"
      }}>{`Link documentation`}</a>{`. To integrate with Embedded Link, visit these `}<a parentName="p" {...{
        "href": "/docs/link/embedded-institution-search/#integration-steps"
      }}>{`integration steps`}</a>{`.`}</p>
    <h5 {...{
      "id": "install-the-link-library"
    }}>{`Install the Link library`}</h5>
    <p>{`You will need to install the Link JavaScript library in order to use Link in your web application.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "switcher=false #index.html",
        "switcher": "false",
        "#index.html": true
      }}>{`<head>
  <title>Extend my Credit</title>
  <script src="https://cdn.plaid.com/link/v2/stable/link-initialize.js"></script>
</head>
`}</code></pre>
    <h5 {...{
      "id": "configure-the-client-side-link-handler"
    }}>{`Configure the client-side Link handler`}</h5>
    <p>{`To run Link, you'll first need to retrieve the `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` from the server as described above. Then call `}<inlineCode parentName="p">{`Plaid.create()`}</inlineCode>{`, passing along that token alongside other callbacks you might want to define. This will return a handler that has an `}<inlineCode parentName="p">{`open`}</inlineCode>{` method. Call that method to open up the Link UI and start the connection process.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "switcher=false #app.js",
        "switcher": "false",
        "#app.js": true
      }}>{`const linkHandler = Plaid.create({
  token: link_token_from_previous_step,
  onSuccess: (public_token, metadata) => {
    // Typically, you'd exchange the public_token for an access token.
    // You won't do that with Plaid Check products.
  },
  onExit: (err, metadata) => {
    // Optionally capture when your user exited the Link flow.
    // Storing this information can be helpful for support.
  },
  onEvent: (eventName, metadata) => {
    // Optionally capture Link flow events, streamed through
    // this callback as your users connect an Item to Plaid Check.
  },
});

linkHandler.open();
`}</code></pre>
    <p>{`If you've used Link in the past with products created by Plaid, you're probably used to taking the `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` received from Link and exchanging it on your server for a persistent `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`. That is not necessary for products created by Plaid Check, and is only necessary if you are using the hybrid flow, where you are using Plaid Inc. products (Transactions, Auth, etc.) alongside Plaid Check products.`}</p>
    <h4 {...{
      "id": "generate-a-report"
    }}>{`Generate a report`}</h4>
    <p>{`Plaid Check will start generating the Consumer Report for your user once they have completed the Link process.`}</p>
    <p>{`Consumer Reports expire 24 hours after they have been created. After that point, you can `}<a parentName="p" {...{
        "href": "/docs/check/add-to-app/#generating-updated-reports"
      }}>{`generate a new report`}</a>{` by calling `}<inlineCode parentName="p">{`/cra/check_report/create`}</inlineCode>{`, passing along the `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` you created earlier.`}</p>
    <h4 {...{
      "id": "listen-for-webhooks"
    }}>{`Listen for webhooks`}</h4>
    <p>{`After a user has finished using Link, it may take some time before the Consumer Report is available for you to retrieve. Listen for the `}<inlineCode parentName="p">{`USER_CHECK_REPORT_READY`}</inlineCode>{` webhook to know when it is safe to request the appropriate set of product data. Integrations created before December 10, 2025 receive the legacy `}<inlineCode parentName="p">{`CHECK_REPORT_READY`}</inlineCode>{` / `}<inlineCode parentName="p">{`CHECK_REPORT_FAILED`}</inlineCode>{` webhooks instead; see `}<a parentName="p" {...{
        "href": "/docs/api/users/user-apis/"
      }}>{`New user APIs`}</a>{` for details.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-node",
        "metastring": "switcher=false #webhookServer.js",
        "switcher": "false",
        "#webhookServer.js": true
      }}>{`app.post('/server/receive_webhook', async (req, res, next) => {
  const product = req.body.webhook_type;
  const code = req.body.webhook_code;
  if (product === 'CHECK_REPORT') {
    if (code === 'USER_CHECK_REPORT_READY') {
      const userId = req.body.user_id;
      // It is now okay to start fetching Consumer Report data for this user
      fetch_cra_report_for_user(userId);
    } else if (code === 'USER_CHECK_REPORT_FAILED') {
      const userId = req.body.user_id;
      // Handle this error appropriately
      report_error_with_generating_report(userId);
    }
  }
  // Handle other types of webhooks
});
`}</code></pre>
    <h4 {...{
      "id": "retrieve-report-data"
    }}>{`Retrieve report data`}</h4>
    <p>{`Once Plaid Check has informed you that your report is ready, you can use one of the Plaid Check product endpoints (`}<inlineCode parentName="p">{`/cra/check_report/base_report/get`}</inlineCode>{`, `}<inlineCode parentName="p">{`/cra/check_report/income_insights/get`}</inlineCode>{`, or `}<inlineCode parentName="p">{`/cra/check_report/partner_insights/get`}</inlineCode>{`) to retrieve the appropriate cash flow insights.`}</p>
    <Callout mdxType="Callout">
      <p>{`Reports expire 24 hours after they have been created (either through a Link session that included the `}<inlineCode parentName="p">{`consumer_report_permissible_purpose`}</inlineCode>{` parameter or through a `}<inlineCode parentName="p">{`/cra/check_report/create`}</inlineCode>{` call). If you don't retrieve the report's data within that time frame, you will need to generate a new Consumer Report.`}</p>
    </Callout>
    <h4 {...{
      "id": "handling-multiple-institutions"
    }}>{`Handling multiple institutions`}</h4>
    <p>{`If you would like your Consumer Report to include information from multiple institutions, the best way is to add the `}<inlineCode parentName="p">{`enable_multi_item_link: true`}</inlineCode>{` flag to your `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` call to enable `}<a parentName="p" {...{
        "href": "/docs/link/multi-item-link/"
      }}>{`Multi-Item Link`}</a>{`. Your user will then have the option to connect to as many institutions as they wish during their Link session, and the generated report will contain data from each institution that the user connects to.`}</p>
    <h5 {...{
      "id": "adding-institutions-after-the-initial-link-session"
    }}>{`Adding institutions after the initial Link session`}</h5>
    <p>{`If your user wants to add additional institutions `}<em parentName="p">{`after`}</em>{` their initial Link session, you may do so by once again generating a Link token (using the same `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` as before, and including at least one Plaid Check product in the products array), and opening a new Link session on the client.`}</p>
    <p>{`Plaid Check will start generating a new report for your user when they are done with a Link session. However, this report will `}<em parentName="p">{`only`}</em>{` contain data from the institution that the user connected to during this most recent Link session.`}</p>
    <p>{`When the user is done, re-generate your Consumer Report by calling `}<inlineCode parentName="p">{`/cra/check_report/create`}</inlineCode>{` as described in the `}<a parentName="p" {...{
        "href": "#generating-updated-reports"
      }}>{`Generating updated reports`}</a>{` section below.`}</p>
    <h5 {...{
      "id": "duplicate-items"
    }}>{`Duplicate Items`}</h5>
    <p>{`If a user has linked `}<a parentName="p" {...{
        "href": "/docs/link/duplicate-items/"
      }}>{`duplicate Items`}</a>{`, the Consumer Report includes only one Item from each set of duplicates, so the same data is not counted twice. Two Items are treated as duplicates if they are at the same institution and either share a login or have at least one account in common; of those, the most recently created Item is used and the rest are excluded. Items that have no accounts, or that are in an error state requiring `}<a parentName="p" {...{
        "href": "/docs/link/update-mode/"
      }}>{`update mode`}</a>{`, are also excluded.`}</p>
    <h4 {...{
      "id": "testing-in-sandbox"
    }}>{`Testing in Sandbox`}</h4>
    <p>{`Because Consumer Report information is heavily dependent on income data, Plaid recommends using the test accounts designed for producing realistic looking income data, such as `}<inlineCode parentName="p">{`user_bank_income`}</inlineCode>{` (with the password `}<inlineCode parentName="p">{`{}`}</inlineCode>{`). The basic Sandbox credentials (`}<inlineCode parentName="p">{`user_good`}</inlineCode>{`/`}<inlineCode parentName="p">{`pass_good`}</inlineCode>{`) do not return good income data.`}</p>
    <p>{`Plaid also has additional test users to represent different levels of creditworthiness. For details, see `}<a parentName="p" {...{
        "href": "/docs/sandbox/test-credentials/#credit-and-income-testing-credentials"
      }}>{`Credit and Income testing credentials`}</a>{`. To learn about creating test users with customized history, see `}<a parentName="p" {...{
        "href": "/docs/sandbox/user-custom/#configuring-the-custom-user-account"
      }}>{`Configuring the custom user account`}</a>{`.`}</p>
 <Callout mdxType="Callout">
      <p>{`When using special Sandbox test credentials (such as `}<inlineCode parentName="p">{`user_bank_income`}</inlineCode>{` / `}<inlineCode parentName="p">{`{}`}</inlineCode>{`), use a non-OAuth test institution, such as First Platypus Bank (ins_109508) or many smaller community banks. Special test credentials may be ignored when using the Sandbox Link OAuth flow.`}</p>
    </Callout>
    <p>{`In the Plaid Account flow, the banks offered to a returning user are the institutions already connected to their `}<a parentName="p" {...{
        "href": "/docs/sandbox/profiles/"
      }}>{`Sandbox Profile`}</a>{`. Selecting one of those reuses the existing connection and skips the credentials screen. To use a `}<a parentName="p" {...{
        "href": "/docs/sandbox/user-custom/"
      }}>{`Sandbox custom user`}</a>{` instead, click "add new account", which lets you enter a custom username; the resulting Item is attached to the profile and is available on subsequent sessions.`}</p>
    <p>{`You cannot reproduce `}<a parentName="p" {...{
        "href": "#duplicate-items"
      }}>{`duplicate Item`}</a>{` handling in Sandbox. Sandbox does not associate Items created with the same test credentials with a single login, so Items that would be duplicates in Production are each treated as distinct and all of them are included in the report.`}</p>
    <h5 {...{
      "id": "testing-without-link"
    }}>{`Testing without Link`}</h5>
    <p>{`Consumer Reports can be generated in Sandbox without going through the Link flow, which is useful for automated testing.`}</p>
    <ol>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/user/create`}</inlineCode>{` with an `}<inlineCode parentName="li">{`identity`}</inlineCode>{` object, as described in `}<a parentName="li" {...{
          "href": "#create-a-user"
        }}>{`Create a user`}</a>{`. Plaid Check requires a user identity; without one, `}<inlineCode parentName="li">{`/cra/check_report/create`}</inlineCode>{` will fail with an `}<inlineCode parentName="li">{`INVALID_FIELD`}</inlineCode>{` error.`}</li>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/sandbox/public_token/create`}</inlineCode>{` with `}<inlineCode parentName="li">{`cra_base_report`}</inlineCode>{` in `}<inlineCode parentName="li">{`initial_products`}</inlineCode>{` and the `}<inlineCode parentName="li">{`user_id`}</inlineCode>{` from the previous step. To supply custom transaction data, set `}<inlineCode parentName="li">{`options.override_username`}</inlineCode>{` to `}<inlineCode parentName="li">{`user_custom`}</inlineCode>{` and pass a `}<a parentName="li" {...{
          "href": "/docs/sandbox/user-custom/"
        }}>{`custom user configuration`}</a>{` as `}<inlineCode parentName="li">{`options.override_password`}</inlineCode>{`.`}</li>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/item/public_token/exchange`}</inlineCode>{` with the returned `}<inlineCode parentName="li">{`public_token`}</inlineCode>{`.`}</li>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/cra/check_report/create`}</inlineCode>{`, listing the products you want to test in the `}<inlineCode parentName="li">{`products`}</inlineCode>{` array. Each product requested this way must also have its own options object in the request; for example, requesting `}<inlineCode parentName="li">{`cra_income_insights`}</inlineCode>{` requires `}<inlineCode parentName="li">{`income_insights.income_insights_version`}</inlineCode>{`.`}</li>
      <li parentName="ol">{`Call the `}<inlineCode parentName="li">{`/get`}</inlineCode>{` endpoint for the product you are testing, such as `}<inlineCode parentName="li">{`/cra/check_report/income_insights/get`}</inlineCode>{`. Until the report has finished generating, this will return a `}<inlineCode parentName="li">{`PRODUCT_NOT_READY`}</inlineCode>{` error.`}</li>
    </ol>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #/cra/check_report/create request for Income Insights testing",
        "switcher": "false",
        "#/cra/check_report/create": true,
        "request": true,
        "for": true,
        "Income": true,
        "Insights": true,
        "testing": true
      }}>{`{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "user_id": "usr_9nSp2KuZ2x4JDw", //use the user_id from the \`/user/create\` call made earlier
  "webhook": "https://www.example.com/webhook",
  "days_requested": 180,
  "consumer_report_permissible_purpose": "EXTENSION_OF_CREDIT",
  "products": ["cra_income_insights"],
  "income_insights": {
    "income_insights_version": "II2"
  }
}
`}</code></pre>
    <h5 {...{
      "id": "testing-servicing"
    }}>{`Testing Servicing`}</h5>
    <p>{`After calling `}<inlineCode parentName="p">{`/cra/servicing/subscription/create`}</inlineCode>{`, use the `}<inlineCode parentName="p">{`/sandbox/cra/servicing/simulate`}</inlineCode>{` endpoint to manually trigger a report generation. Plaid generates a report covering every product on the subscription, fires a `}<inlineCode parentName="p">{`CRA_REPORT_UPDATED`}</inlineCode>{` webhook, and returns a `}<inlineCode parentName="p">{`report_id`}</inlineCode>{` that you can pass to `}<inlineCode parentName="p">{`/cra/check_report/base_report/get`}</inlineCode>{` or `}<inlineCode parentName="p">{`/cra/check_report/income_insights/get`}</inlineCode>{`. For details, see `}<a parentName="p" {...{
        "href": "/docs/check/servicing/#testing-in-sandbox"
      }}>{`Servicing: Testing in Sandbox`}</a>{`.`}</p>
    <h5 {...{
      "id": "testing-income-insights"
    }}>{`Testing Income Insights`}</h5>
    <p>{`Use the `}<a parentName="p" {...{
        "href": "/docs/sandbox/test-credentials/#credit-and-income-testing-credentials"
      }}>{`Sandbox credit testing credentials`}</a>{` with a non-OAuth test institution, such as First Platypus Bank, for more realistic and valid data.`}</p>
    <p>{`To customize the employer name during testing, you can test with a custom Sandbox user, following the custom Sandbox user `}<a parentName="p" {...{
        "href": "/docs/sandbox/user-custom/"
      }}>{`documentation`}</a>{` and `}<a parentName="p" {...{
        "href": "https://github.com/plaid/sandbox-custom-users/"
      }}>{`examples`}</a>{`. When creating an income transaction for this user:`}</p>
    <ul>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`amount`}</inlineCode>{` must be negative, so that the transaction will represent income.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`date_transacted`}</inlineCode>{` and `}<inlineCode parentName="li">{`date_posted`}</inlineCode>{` must fall within the `}<inlineCode parentName="li">{`cra_options.days_requested`}</inlineCode>{` range of the `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{` call.`}</li>
      <li parentName="ul">{`the `}<inlineCode parentName="li">{`description`}</inlineCode>{` field must contain the desired employer name, followed by `}<inlineCode parentName="li">{` Direct Dep`}</inlineCode>{`.`}</li>
    </ul>
    <p>{`The example transaction below will generate an income stream with the employer name `}<inlineCode parentName="p">{`Weyland-Yutani`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "switcher=false #Sample income transaction for testing employer name",
        "switcher": "false",
        "#Sample": true,
        "income": true,
        "transaction": true,
        "for": true,
        "testing": true,
        "employer": true,
        "name": true
      }}>{`{
  "date_transacted": "2025-08-01",
  "date_posted": "2025-08-03",
  "amount": -5500,
  "description": "Weyland-Yutani Direct Dep",
  "currency": "USD"
}
`}</code></pre>
    <h4 {...{
      "id": "generating-updated-reports"
    }}>{`Generating updated reports`}</h4>
    <p>{`If, after some time has passed, you wish to generate a new report with updated data (or a user has connected additional institutions), you can do so by performing the following steps:`}</p>
    <ol>
      <li parentName="ol">{`Generate the new report by calling `}<inlineCode parentName="li">{`/cra/check_report/create`}</inlineCode>{` with the same `}<inlineCode parentName="li">{`user_id`}</inlineCode>{` you used earlier.`}</li>
      <li parentName="ol">{`Wait for the `}<inlineCode parentName="li">{`USER_CHECK_REPORT_READY`}</inlineCode>{` webhook.`}</li>
      <li parentName="ol">{`Once you have received the webhook, use one of the Plaid Check product endpoints (`}<inlineCode parentName="li">{`/cra/check_report/base_report/get`}</inlineCode>{`, `}<inlineCode parentName="li">{`/cra/check_report/income_insights/get`}</inlineCode>{`, or `}<inlineCode parentName="li">{`/cra/check_report/partner_insights/get`}</inlineCode>{`) to retrieve the corresponding product data.`}</li>
    </ol>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "install-and-initialize-plaid-libraries",
        level: 4,
        title: "Install and initialize Plaid libraries",
      },
{
        id: "create-a-user",
        level: 4,
        title: "Create a user",
      },
{
        id: "create-a-link-token",
        level: 4,
        title: "Create a Link token",
      },
{
        id: "initialize-link",
        level: 4,
        title: "Initialize Link",
      },
{
        id: "generate-a-report",
        level: 4,
        title: "Generate a report",
      },
{
        id: "listen-for-webhooks",
        level: 4,
        title: "Listen for webhooks",
      },
{
        id: "retrieve-report-data",
        level: 4,
        title: "Retrieve report data",
      },
{
        id: "handling-multiple-institutions",
        level: 4,
        title: "Handling multiple institutions",
      },
{
        id: "testing-in-sandbox",
        level: 4,
        title: "Testing in Sandbox",
      },
{
        id: "generating-updated-reports",
        level: 4,
        title: "Generating updated reports",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
